import React, { Suspense, useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { LayoutProvider } from './components/Layout/LayoutContext';
import { LoadingSpinner } from './components/Shared/LoadingSpinner/LoadingSpinner';
import { AppContext } from './context/app-context';
import { useEnvironment } from './hooks/useEnvironment';
import { UserStatus } from './models';
import { Applications } from './pages/Applications/ApplicationsRouter';
import { ErrorPage } from './pages/Error/ErrorPage';
import { MockFormsBoundary } from './pages/Forms/mock';

const ChangeRequests = React.lazy(() => import('./pages/ChangeRequests/ChangeRequestsRouter'));
const ApplyForChange = React.lazy(() => import('./pages/ApplyForChange/ApplyForChangeRouter'));
const Forms = React.lazy(() => import('./pages/Forms/FormsRouter'));
const MockFormsIndexPage = React.lazy(() => import('./pages/Forms/mock/MockFormsIndexPage').then((module) => ({ default: module.MockFormsIndexPage })));
const BankAccounts = React.lazy(() => import('./pages/BankAccounts/BankAccountsRouter'));
const CaseManagement = React.lazy(() => import('./pages/CaseManagement/CaseManagementRouter'));
const Documents = React.lazy(() => import('./pages/Documents/DocumentsRouter'));
const AppealDecision = React.lazy(() => import('./pages/AppealDecision/AppealDecisionRouter'));
const GeneralApplicationForms = React.lazy(() => import('./pages/GeneralApplications/GeneralApplicationsRouter'));

export default function App() {
  const { state } = useContext(AppContext);
  const { isProd } = useEnvironment();

  if (state.user.status !== UserStatus.LOGGED_IN) return <div>User not signed in</div>;

  return (
    <LayoutProvider defaultLayout="main">
      <Layout>
        <Routes>
          <Route path="/error" element={<ErrorPage />} />
          <>
            <Route path="/" element={<Navigate replace to="/bankaccounts" />} />
            <Route
              path="/bankaccounts/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <BankAccounts />
                </Suspense>
              }
            />
            <Route
              path="/apply-for-change/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ApplyForChange />
                </Suspense>
              }
            />
            <Route
              path="/changerequests/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ChangeRequests />
                </Suspense>
              }
            />
            <Route
              path="/casemanagement/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CaseManagement />
                </Suspense>
              }
            />
            <Route
              path="/documents/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Documents />
                </Suspense>
              }
            />
            <Route
              path="/appeal-decision/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AppealDecision />
                </Suspense>
              }
            />
            <Route
              path="/forms/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Forms />
                </Suspense>
              }
            />
            {!isProd && (
              <>
                <Route
                  path="/mock-forms"
                  element={
                    <MockFormsBoundary>
                      <Suspense fallback={<LoadingSpinner />}>
                        <MockFormsIndexPage />
                      </Suspense>
                    </MockFormsBoundary>
                  }
                />
                <Route
                  path="/mock-forms/*"
                  element={
                    <MockFormsBoundary>
                      <Suspense fallback={<LoadingSpinner />}>
                        <Forms />
                      </Suspense>
                    </MockFormsBoundary>
                  }
                />
              </>
            )}
            <Route
              path="/applications/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Applications />
                </Suspense>
              }
            />
            <Route
              path="/generalapplications/*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <GeneralApplicationForms />
                </Suspense>
              }
            />
          </>
        </Routes>
      </Layout>
    </LayoutProvider>
  );
}
